import { ArrowDown, ArrowUp, Plus, X } from "lucide-react";
import * as React from "react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardBody, CardFooter, CardHeader } from "@/components/ui/card";
import { Callout, ErrorState, Skeleton } from "@/components/ui/feedback";
import { useToast } from "@/components/ui/toast";
import { ApiError } from "@/lib/api";
import { dispositionKindText, moveItem, sameOrder } from "@/lib/campaign";
import {
  useCampaignDispositions, useDispositions, useSetCampaignDispositions,
} from "@/lib/queries";
import type { Campaign, Disposition } from "@/lib/types";

/**
 * The outcomes a rep on this campaign chooses from, in the order their buttons appear (ADR-017).
 *
 * A pick from the tenant's catalog, never new entries: a disposition's slug is what the contract,
 * the CRM's tags and the reports carry, so the catalog is one list the tenant shares. While a
 * campaign has picked nothing its reps see the whole active catalog in catalog order, and saving
 * an empty pick goes back to that.
 *
 * Order is a preference, not a set, so the pick is built and reordered explicitly — the same
 * shape as a domain's outbound trunks — and saved as a whole.
 */
export function CampaignDispositions({
  campaign, canManage,
}: {
  campaign: Campaign;
  canManage: boolean;
}) {
  const toast = useToast();
  const catalog = useDispositions(campaign.tenant_id);
  const effective = useCampaignDispositions(campaign.id);
  const save = useSetCampaignDispositions();
  /** The pick being edited; null while nothing has been touched. */
  const [draft, setDraft] = React.useState<number[] | null>(null);

  if (catalog.isPending || effective.isPending) {
    return (
      <Card>
        <CardBody className="space-y-2">
          <Skeleton className="h-9" />
          <Skeleton className="h-9" />
          <Skeleton className="h-9" />
        </CardBody>
      </Card>
    );
  }

  if (!catalog.data || !effective.data) {
    return (
      <Card>
        <ErrorState
          error={catalog.error ?? effective.error}
          title="Could not load the dispositions"
          action={
            <Button
              variant="outline"
              onClick={() => {
                void catalog.refetch();
                void effective.refetch();
              }}
            >
              Try again
            </Button>
          }
        />
      </Card>
    );
  }

  const saved = effective.data.map((d) => d.id);
  const ids = draft ?? saved;
  const byId = new Map([...catalog.data, ...effective.data].map((d) => [d.id, d]));
  const picked = ids.map((id) => byId.get(id)).filter((d): d is Disposition => d !== undefined);
  const available = catalog.data.filter((d) => !ids.includes(d.id));
  const wholeCatalog = draft === null && sameOrder(saved, catalog.data.map((d) => d.id));
  const dirty = draft !== null && !sameOrder(draft, saved);

  const persist = async (next: number[], done: string) => {
    try {
      await save.mutateAsync({ campaignId: campaign.id, ids: next });
      setDraft(null);
      toast.success(done, campaign.name);
    } catch (e) {
      toast.error(
        "Could not save the dispositions",
        e instanceof ApiError ? e.message : "Please try again.",
      );
    }
  };

  return (
    <Card className="overflow-hidden">
      <CardHeader
        title="Dispositions"
        description="The outcomes a rep chooses from after a call on this campaign, in the order their buttons appear."
        action={
          canManage &&
          !wholeCatalog &&
          draft === null && (
            <Button
              variant="outline"
              size="sm"
              loading={save.isPending}
              onClick={() => void persist([], "Reps see the whole catalog")}
            >
              Use the whole catalog
            </Button>
          )
        }
      />

      <CardBody className="grid gap-6 lg:grid-cols-2">
        <section className="space-y-2">
          <h3 className="text-xs font-medium text-text">
            {wholeCatalog
              ? "The whole catalog, in catalog order"
              : "The campaign's pick, in order"}
          </h3>
          {picked.length === 0 ? (
            <Callout tone="warning">
              {draft === null
                ? "Reps on this campaign have no disposition to choose."
                : "Nothing picked. Saving this gives reps the whole catalog."}
            </Callout>
          ) : (
            <ol className="space-y-1.5">
              {picked.map((d, i) => (
                <li
                  key={d.id}
                  className="flex items-center gap-2 rounded-md border border-border px-3 py-2"
                >
                  <span className="w-4 text-xs tabular-nums text-text-subtle">{i + 1}</span>
                  <DispositionLabel disposition={d} />
                  {canManage && (
                    <>
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        aria-label={`Move ${d.label} up`}
                        disabled={i === 0}
                        onClick={() => setDraft(moveItem(ids, i, -1))}
                      >
                        <ArrowUp />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        aria-label={`Move ${d.label} down`}
                        disabled={i === picked.length - 1}
                        onClick={() => setDraft(moveItem(ids, i, 1))}
                      >
                        <ArrowDown />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        aria-label={`Remove ${d.label}`}
                        onClick={() => setDraft(ids.filter((id) => id !== d.id))}
                      >
                        <X />
                      </Button>
                    </>
                  )}
                </li>
              ))}
            </ol>
          )}
        </section>

        {canManage && (
          <section className="space-y-2">
            <div className="flex items-center justify-between gap-2">
              <h3 className="text-xs font-medium text-text">In the catalog, not picked</h3>
              {/* An outcome a campaign lacks is added to the catalog, never to one campaign: its
                  slug is what the contract, the CRM's tags and the reports carry. The tenant rides
                  along for a superadmin, whose catalog is whichever tenant they name. */}
              <Link
                to={`/dispositions?tenant=${campaign.tenant_id}`}
                className="text-xs text-accent-11 underline-offset-4 hover:underline"
              >
                Edit the catalog
              </Link>
            </div>
            {available.length === 0 ? (
              <p className="rounded-md border border-dashed border-border px-3 py-4 text-center text-xs text-text-muted">
                Every disposition in the catalog is picked.
              </p>
            ) : (
              <ul className="space-y-1.5">
                {available.map((d) => (
                  <li
                    key={d.id}
                    className="flex items-center gap-2 rounded-md border border-border px-3 py-2"
                  >
                    <DispositionLabel disposition={d} />
                    <Button variant="outline" size="sm" onClick={() => setDraft([...ids, d.id])}>
                      <Plus />
                      Add
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}
      </CardBody>

      {canManage && dirty && (
        <CardFooter>
          <Button variant="outline" onClick={() => setDraft(null)} disabled={save.isPending}>
            Discard
          </Button>
          <Button
            variant="default"
            loading={save.isPending}
            onClick={() => void persist(ids, "Dispositions saved")}
          >
            Save dispositions
          </Button>
        </CardFooter>
      )}
    </Card>
  );
}

function DispositionLabel({ disposition: d }: { disposition: Disposition }) {
  return (
    <span className="flex min-w-0 flex-1 flex-wrap items-center gap-x-1.5 gap-y-0.5">
      <span className="text-xs font-medium text-text">{d.label}</span>
      <span className="font-mono text-xs text-text-muted">{d.slug}</span>
      <Badge tone="neutral" size="sm">
        {dispositionKindText(d.kind)}
      </Badge>
      {d.requires_note && (
        <Badge tone="outline" size="sm">
          Needs a note
        </Badge>
      )}
    </span>
  );
}
